import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Send, Eye, Mail, Loader2, CheckCircle2, Clock } from "lucide-react";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  fetchBroadcastCampaigns,
  fetchBroadcastPreview,
  sendBroadcast,
  previewBroadcastAudience,
  fetchTrialNudgePreview,
  sendTrialNudge,
} from "@/lib/api";
import { toast } from "sonner";

const SEGMENT_META = [
  { id: "never_paid", label: "Never paid", tone: "muted", tagline: "Joined but not converted" },
  { id: "cancelled", label: "Cancelled", tone: "warn", tagline: "Churned subscribers" },
  { id: "paused", label: "Paused", tone: "warn", tagline: "Temporarily off" },
  { id: "active", label: "Active", tone: "ok", tagline: "Paying subscribers" },
];

/**
 * AdminBroadcasts — fire one-time announcement emails (e.g., after a
 * production deploy). Every send is idempotent per user, so a re-hit
 * only mails the users who haven't received the campaign yet.
 * The type-to-confirm gate ("send") prevents accidental fires.
 */
export default function AdminBroadcasts() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewId, setPreviewId] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmById, setConfirmById] = useState({});
  const [sendingId, setSendingId] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [segmentsById, setSegmentsById] = useState({});     // campaign_id → Set<segment>
  const [audienceById, setAudienceById] = useState({});     // campaign_id → { matches, by_segment }
  const [audienceLoading, setAudienceLoading] = useState({});
  const [globalSegments, setGlobalSegments] = useState({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchBroadcastCampaigns();
      setCampaigns(r.campaigns || []);
      setGlobalSegments(r.segments || {});
    } catch (e) {
      console.error(e);
      toast.error("Could not load campaigns.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const toggleSegment = (cid, seg) => {
    setSegmentsById((prev) => {
      const cur = new Set(prev[cid] || []);
      if (cur.has(seg)) cur.delete(seg); else cur.add(seg);
      return { ...prev, [cid]: cur };
    });
  };

  // Debounced audience preview whenever segments for this campaign change
  useEffect(() => {
    const pending = Object.keys(segmentsById);
    if (!pending.length) return;
    const t = setTimeout(async () => {
      for (const cid of pending) {
        const segs = Array.from(segmentsById[cid] || []);
        setAudienceLoading((s) => ({ ...s, [cid]: true }));
        try {
          const p = await previewBroadcastAudience({
            campaign_id: cid,
            segments: segs.length ? segs : null,
          });
          setAudienceById((s) => ({ ...s, [cid]: p }));
        } catch (e) {
          console.error(e);
        } finally {
          setAudienceLoading((s) => ({ ...s, [cid]: false }));
        }
      }
    }, 400);
    return () => clearTimeout(t);
  }, [segmentsById]);

  const openPreview = async (cid) => {
    setPreviewId(cid);
    setPreviewLoading(true);
    setPreviewHtml("");
    try {
      const r = await fetchBroadcastPreview(cid);
      setPreviewHtml(r.html_preview);
      setPreviewSubject(r.subject);
    } catch (e) {
      console.error(e);
      toast.error("Could not load preview.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async (cid) => {
    const phrase = (confirmById[cid] || "").trim().toLowerCase();
    if (phrase !== "send") {
      toast.error('Type the word "send" to confirm.');
      return;
    }
    const segs = Array.from(segmentsById[cid] || []);
    setSendingId(cid);
    setLastResult(null);
    try {
      const r = await sendBroadcast({
        campaign_id: cid,
        confirm_phrase: "send",
        segments: segs.length ? segs : null,
      });
      setLastResult(r);
      toast.success(`Sent ${r.sent} · failed ${r.failed}${r.skipped_by_segment ? ` · ${r.skipped_by_segment} skipped by segment` : ""}`);
      setConfirmById((s) => ({ ...s, [cid]: "" }));
      await refresh();
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail || "Broadcast failed.";
      toast.error(detail);
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-200 transition-colors"
          data-testid="back-to-admin"
        >
          <ChevronLeft className="h-3 w-3" /> Admin
        </Link>
        <div className="flex items-center gap-4">
          <AdminToggle />
          <UserBadge />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-10 sm:pt-16 pb-24">
        <p className="font-sans uppercase tracking-[0.24em] sm:tracking-[0.28em] text-xs text-terracotta mb-3 sm:mb-4">
          Umbra · Broadcasts
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-bone-100 leading-tight mb-4">
          One-time email campaigns.
        </h1>
        <p className="font-serif italic text-base sm:text-lg text-bone-400 max-w-2xl leading-relaxed mb-10">
          Each campaign fires once per user, forever. Re-hit safely — Umbra only mails accounts
          that have not yet received it. Type <span className="text-terracotta">send</span> to confirm.
        </p>

        {loading ? (
          <p className="font-sans text-bone-400">Loading campaigns…</p>
        ) : (
          <>
            <TrialNudgeCard />
            {campaigns.length === 0 ? (
              <p className="font-sans text-bone-400">No campaigns available.</p>
            ) : (
              <div className="space-y-6" data-testid="broadcast-campaigns">
            {campaigns.map((c) => {
              const isSending = sendingId === c.campaign_id;
              const done = c.pending === 0 && c.already_sent > 0;
              return (
                <div
                  key={c.campaign_id}
                  data-testid={`broadcast-card-${c.campaign_id}`}
                  className="border border-obsidian-600 bg-obsidian-800 p-5 sm:p-6"
                >
                  <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <p className="font-sans uppercase tracking-[0.2em] text-[0.6rem] text-terracotta mb-2">
                        Campaign · {c.campaign_id}
                      </p>
                      <p className="font-serif text-xl sm:text-2xl text-bone-100 leading-tight">
                        {c.subject}
                      </p>
                    </div>
                    {done && (
                      <span className="inline-flex items-center gap-2 font-sans text-[0.65rem] uppercase tracking-[0.24em] text-terracotta border border-terracotta/40 px-3 py-1">
                        <CheckCircle2 className="h-3 w-3" /> Fully sent
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-6 mb-5 font-sans text-xs uppercase tracking-[0.2em] text-bone-500 flex-wrap">
                    <span>Already sent · <span className="text-bone-200">{c.already_sent}</span></span>
                    <span>Pending · <span className={c.pending > 0 ? "text-terracotta" : "text-bone-200"}>{c.pending}</span></span>
                  </div>

                  {/* Segment multi-select */}
                  <div className="mb-5" data-testid={`broadcast-segments-${c.campaign_id}`}>
                    <p className="font-sans uppercase tracking-[0.2em] text-[0.55rem] text-bone-500 mb-2">
                      Send only to (leave empty for everyone pending)
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {SEGMENT_META.map((s) => {
                        const active = (segmentsById[c.campaign_id] || new Set()).has(s.id);
                        const count = globalSegments[s.id] ?? 0;
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => toggleSegment(c.campaign_id, s.id)}
                            data-testid={`broadcast-segment-${c.campaign_id}-${s.id}`}
                            className={`text-left p-3 border transition-colors ${
                              active
                                ? "border-terracotta bg-obsidian-900 text-terracotta"
                                : "border-obsidian-600 bg-obsidian-900 text-bone-400 hover:border-terracotta/60"
                            }`}
                          >
                            <p className="font-sans text-xs uppercase tracking-[0.16em]">{s.label}</p>
                            <p className={`font-serif text-lg mt-0.5 ${active ? "text-terracotta" : "text-bone-200"}`}>{count}</p>
                            <p className="font-sans text-[0.55rem] text-bone-500 leading-tight">{s.tagline}</p>
                          </button>
                        );
                      })}
                    </div>
                    {(segmentsById[c.campaign_id]?.size ?? 0) > 0 && (
                      <p
                        className="mt-3 font-sans text-xs text-bone-400"
                        data-testid={`broadcast-audience-count-${c.campaign_id}`}
                      >
                        {audienceLoading[c.campaign_id] ? (
                          <span>Counting audience…</span>
                        ) : audienceById[c.campaign_id] ? (
                          <>
                            <span className="text-terracotta">{audienceById[c.campaign_id].matches}</span>
                            {" "}journalers will receive this send
                          </>
                        ) : null}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-3 flex-wrap mb-3">
                    <Button
                      variant="outline"
                      onClick={() => openPreview(c.campaign_id)}
                      data-testid={`broadcast-preview-${c.campaign_id}`}
                      className="rounded-none border-obsidian-600 hover:border-terracotta bg-transparent text-bone-200 hover:text-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs px-4 py-3 min-h-[44px] transition-colors"
                    >
                      <Eye className="h-4 w-4 mr-2" /> Preview
                    </Button>
                    <Input
                      value={confirmById[c.campaign_id] || ""}
                      onChange={(e) =>
                        setConfirmById((s) => ({ ...s, [c.campaign_id]: e.target.value }))
                      }
                      placeholder='Type "send" to confirm'
                      data-testid={`broadcast-confirm-${c.campaign_id}`}
                      className="rounded-none bg-obsidian-900 border-obsidian-600 focus-visible:ring-1 focus-visible:ring-terracotta text-bone-100 font-sans text-sm max-w-[220px]"
                      disabled={isSending || c.pending === 0}
                    />
                    <Button
                      onClick={() => handleSend(c.campaign_id)}
                      disabled={
                        isSending ||
                        c.pending === 0 ||
                        (confirmById[c.campaign_id] || "").trim().toLowerCase() !== "send"
                      }
                      data-testid={`broadcast-send-${c.campaign_id}`}
                      className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans uppercase tracking-wider text-xs px-5 py-3 min-h-[44px] transition-colors"
                    >
                      {isSending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      Send to {(segmentsById[c.campaign_id]?.size ?? 0) > 0 && audienceById[c.campaign_id]
                        ? audienceById[c.campaign_id].matches
                        : c.pending}
                    </Button>
                  </div>

                  {lastResult && lastResult.campaign_id === c.campaign_id && (
                    <div
                      data-testid={`broadcast-result-${c.campaign_id}`}
                      className="mt-3 font-sans text-xs text-bone-400 space-y-2"
                    >
                      <p>
                        Just sent: <span className="text-bone-200">{lastResult.sent}</span> delivered ·{" "}
                        <span className={lastResult.failed ? "text-terracotta" : "text-bone-200"}>
                          {lastResult.failed}
                        </span>{" "}
                        failed
                        {lastResult.skipped_by_segment ? ` · ${lastResult.skipped_by_segment} skipped by segment` : ""}
                        {lastResult.skipped_invalid_email ? ` · ${lastResult.skipped_invalid_email} invalid email` : ""}.
                      </p>
                      {lastResult.errors && lastResult.errors.length > 0 && (
                        <details
                          className="border border-terracotta/30 bg-obsidian-900 p-3"
                          data-testid={`broadcast-errors-${c.campaign_id}`}
                        >
                          <summary className="cursor-pointer font-sans uppercase tracking-[0.2em] text-[0.6rem] text-terracotta">
                            Why {lastResult.failed + (lastResult.skipped_invalid_email || 0)} failed — view details
                          </summary>
                          <ul className="mt-2 space-y-1 font-sans text-[0.7rem] text-bone-300 max-h-48 overflow-y-auto">
                            {lastResult.errors.map((err, i) => (
                              <li key={i} className="border-l-2 border-terracotta/40 pl-2">
                                <span className="text-bone-100">{err.email || "(no email)"}</span> —{" "}
                                <span className="text-bone-400">{err.error}</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {previewId && (
          <div
            data-testid="broadcast-preview-modal"
            className="fixed inset-0 z-50 bg-obsidian-900/85 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
            onClick={() => setPreviewId(null)}
          >
            <div
              className="w-full max-w-2xl bg-obsidian-800 border border-obsidian-600 my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-4 border-b border-obsidian-600 p-5">
                <div className="min-w-0">
                  <p className="font-sans uppercase tracking-[0.2em] text-[0.6rem] text-terracotta mb-1">
                    Preview
                  </p>
                  <p className="font-serif text-lg text-bone-100 truncate flex items-center gap-2">
                    <Mail className="h-4 w-4 shrink-0" /> {previewSubject}
                  </p>
                </div>
                <button
                  onClick={() => setPreviewId(null)}
                  className="font-sans uppercase tracking-[0.2em] text-xs text-bone-400 hover:text-terracotta transition-colors min-h-[44px] px-2"
                  data-testid="broadcast-preview-close"
                >
                  Close
                </button>
              </div>
              <div className="p-2 sm:p-4">
                {previewLoading ? (
                  <div className="flex items-center gap-3 p-8">
                    <Loader2 className="h-4 w-4 animate-spin text-terracotta" />
                    <p className="font-sans text-bone-400">Rendering preview…</p>
                  </div>
                ) : (
                  <iframe
                    title="broadcast-preview"
                    srcDoc={previewHtml}
                    className="w-full h-[70vh] bg-obsidian-900 border-0"
                    data-testid="broadcast-preview-iframe"
                  />
                )}
              </div>
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}

function TrialNudgeCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [confirm, setConfirm] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const p = await fetchTrialNudgePreview();
      setPreview(p);
    } catch {
      toast.error("Could not load trial-nudge audience.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleSend = async () => {
    if (confirm.trim().toLowerCase() !== "send") {
      toast.error('Type "send" to confirm.');
      return;
    }
    try {
      setSending(true);
      const r = await sendTrialNudge();
      setLastResult(r);
      toast.success(`Sent ${r.sent} · failed ${r.failed}`);
      setConfirm("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trial-nudge send failed.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="border border-terracotta bg-terracotta/5 p-5 sm:p-6 mb-8"
      data-testid="trial-nudge-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="min-w-0">
          <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-2 flex items-center gap-2">
            <Clock className="h-3 w-3" /> Trial nudge · Day 5
          </p>
          <h2 className="font-serif text-2xl text-bone-100">
            Warm reminder before Chapter 2 relocks
          </h2>
          <p className="font-serif italic text-sm text-bone-400 mt-1 max-w-xl">
            Fires to users on day 4–6 of a still-active 7-day trial. Idempotent —
            safe to run daily so everyone catches their window.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="flex items-center gap-2 font-sans text-sm text-bone-500">
          <Loader2 className="h-3 w-3 animate-spin" /> counting audience
        </p>
      ) : (
        <>
          <div
            className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5"
            data-testid="trial-nudge-stats"
          >
            <div className="border border-obsidian-600 bg-obsidian-900 p-3">
              <p className="font-sans uppercase tracking-[0.2em] text-[0.55rem] text-bone-500 mb-1">
                Pending
              </p>
              <p
                className="font-serif text-3xl text-terracotta"
                data-testid="trial-nudge-pending"
              >
                {preview.pending}
              </p>
            </div>
            <div className="border border-obsidian-600 bg-obsidian-900 p-3">
              <p className="font-sans uppercase tracking-[0.2em] text-[0.55rem] text-bone-500 mb-1">
                Already mailed
              </p>
              <p className="font-serif text-3xl text-bone-300">
                {preview.already_sent}
              </p>
            </div>
            <div className="border border-obsidian-600 bg-obsidian-900 p-3 col-span-2 sm:col-span-1">
              <p className="font-sans uppercase tracking-[0.2em] text-[0.55rem] text-bone-500 mb-1">
                Sample
              </p>
              <p className="font-mono text-xs text-bone-300 truncate">
                {preview.sample.length === 0
                  ? "—"
                  : preview.sample.map((s) => s.email).join(", ")}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder='Type "send" to confirm'
              disabled={preview.pending === 0 || sending}
              data-testid="trial-nudge-confirm-input"
              className="flex-1 min-w-[220px] bg-obsidian-900 border-obsidian-600 text-bone-100 rounded-none focus-visible:ring-1 focus-visible:ring-terracotta"
            />
            <Button
              onClick={handleSend}
              disabled={preview.pending === 0 || sending}
              data-testid="trial-nudge-send-btn"
              className="rounded-none bg-terracotta hover:bg-terracotta-deep disabled:bg-obsidian-700 disabled:text-bone-500 text-bone-100 font-sans uppercase tracking-wider text-xs px-5 py-3 min-h-[44px]"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Fire trial nudge
            </Button>
          </div>
          {preview.pending === 0 && (
            <p className="font-sans text-xs text-bone-500 mt-3" data-testid="trial-nudge-empty">
              No trial users in the day-5 window right now.
            </p>
          )}
          {lastResult && (
            <div
              className="mt-4 font-sans text-xs text-bone-400"
              data-testid="trial-nudge-result"
            >
              Just fired: <span className="text-bone-200">{lastResult.sent}</span>{" "}
              delivered ·{" "}
              <span className={lastResult.failed ? "text-terracotta" : "text-bone-200"}>
                {lastResult.failed}
              </span>{" "}
              failed
              {lastResult.skipped_invalid_email
                ? ` · ${lastResult.skipped_invalid_email} invalid`
                : ""}
              .
            </div>
          )}
        </>
      )}
    </div>
  );
}
